import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faEye } from '@fortawesome/free-solid-svg-icons';
import { copyToClipboard } from '../utils/clipboard';
import {
  buildComment,
  isAbsent,
  needsEvaluation,
  stripRollFromName,
  studentEdupageClipboard
} from '../utils/mcqMatch';

// Student-centric class table. Shown on the Check screen once every
// extracted sheet uniquely matches a student and the roll / variant / name
// cross-checks are clean. Students with no sheet stay here as absent rows.

function answerTone(display, hasSheet) {
  if (!hasSheet) return { bg: 'transparent', color: '#9ca3af' };
  const isValidLetter = /^[A-D]$/i.test(display);
  const isMulti = !isValidLetter && (display.includes(',') || /[A-D].*[A-D]/i.test(display));
  const isProblem = !isValidLetter && !isMulti;
  if (isProblem) return { bg: 'rgba(59,130,246,0.40)', color: '#1e3a8a' };
  if (isMulti) return { bg: 'rgba(249,115,22,0.45)', color: '#7c2d12' };
  return { bg: 'transparent', color: display ? '#111827' : '#9ca3af' };
}

function variantTone(value, maxVariant) {
  const s = String(value || '').trim().toUpperCase();
  if (!s) return { bg: 'rgba(239,68,68,0.30)', color: '#991b1b' };
  const ch = s[0];
  if (maxVariant && /[A-Z]/.test(ch) && ch > maxVariant) {
    return { bg: 'rgba(249,115,22,0.40)', color: '#9a3412' };
  }
  return { bg: 'transparent', color: '#111827' };
}

// Walk from the open input to the next VAR / answer cell. Read-only columns
// (name, roll, sheet, comment) have no editable child, so the walk skips them.
function neighborEditKey(input, dir) {
  const td = input.closest('td');
  if (!td) return null;
  const tr = td.parentElement;
  if (!tr) return null;

  const keyOf = (cell) => {
    const target = cell && cell.querySelector('[data-editable-cell="1"]');
    if (!target) return null;
    return { row: target.dataset.editRow, col: target.dataset.editCol };
  };

  if (dir === 'left' || dir === 'right') {
    let candidate = dir === 'right' ? td.nextElementSibling : td.previousElementSibling;
    while (candidate) {
      const key = keyOf(candidate);
      if (key) return key;
      candidate = dir === 'right' ? candidate.nextElementSibling : candidate.previousElementSibling;
    }
    return null;
  }

  const tdIndex = Array.from(tr.children).indexOf(td);
  let targetTr = dir === 'down' ? tr.nextElementSibling : tr.previousElementSibling;
  while (targetTr) {
    const key = keyOf(targetTr.children[tdIndex]);
    if (key) return key;
    targetTr = dir === 'down' ? targetTr.nextElementSibling : targetTr.previousElementSibling;
  }
  return null;
}

function EditableCell({ value, onCommit, align = 'center', color, fontWeight = 600, rowKey, colKey }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const cancelRef = useRef(false);
  const committedRef = useRef(false);

  const open = () => {
    cancelRef.current = false;
    committedRef.current = false;
    setDraft(value || '');
    setEditing(true);
  };

  const cellProps = {
    'data-editable-cell': '1',
    'data-edit-row': rowKey,
    'data-edit-col': colKey
  };

  if (!editing) {
    return (
      <span
        {...cellProps}
        onClick={open}
        title="Click to edit · Arrow keys move to the next cell"
        style={{
          display: 'inline-block',
          minWidth: 18,
          cursor: 'text',
          color: color || 'inherit',
          fontWeight,
          textAlign: align
        }}
      >
        {value == null || value === '' ? '·' : String(value)}
      </span>
    );
  }

  // Save once. Blur also fires when the input unmounts after an arrow move,
  // so the second call must not write the value again.
  const commit = () => {
    if (cancelRef.current) {
      cancelRef.current = false;
      setEditing(false);
      return;
    }
    setEditing(false);
    if (committedRef.current) return;
    committedRef.current = true;
    if ((draft || '') !== (value || '')) onCommit(draft);
  };

  // These cells hold one letter. Any arrow saves it and opens the neighbor,
  // including when the whole letter is selected.
  const commitAndMove = (input, dir) => {
    const next = neighborEditKey(input, dir);
    commit();
    if (!next) return;
    requestAnimationFrame(() => {
      const el = document.querySelector(
        `.mcq-class-table [data-edit-row="${CSS.escape(next.row)}"][data-edit-col="${CSS.escape(next.col)}"]`
      );
      if (el) el.click();
    });
  };

  return (
    <input
      {...cellProps}
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={(e) => e.target.select()}
      onMouseUp={(e) => e.preventDefault()}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); commit(); return; }
        if (e.key === 'Escape') {
          e.preventDefault();
          cancelRef.current = true;
          setDraft(value || '');
          setEditing(false);
          return;
        }
        if (e.key === 'ArrowUp') { e.preventDefault(); commitAndMove(e.currentTarget, 'up'); return; }
        if (e.key === 'ArrowDown') { e.preventDefault(); commitAndMove(e.currentTarget, 'down'); return; }
        if (e.key === 'ArrowLeft') { e.preventDefault(); commitAndMove(e.currentTarget, 'left'); return; }
        if (e.key === 'ArrowRight') { e.preventDefault(); commitAndMove(e.currentTarget, 'right'); return; }
      }}
      style={{
        width: 36,
        textAlign: align,
        fontWeight,
        color: color || 'inherit',
        border: '1px solid #93c5fd',
        borderRadius: 4,
        padding: '1px 2px'
      }}
    />
  );
}

export default function McqClassTable({
  fillHeight = false,
  students,
  match,
  mcqCount,
  maxVariant,
  onUpdateField,
  onUpdateAnswer,
  onUnmatch,
  openPreview,
  selectedRowIds,
  onToggleRow
}) {
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [hoveredColIdx, setHoveredColIdx] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const copiedTimer = useRef(null);
  const questionCount = mcqCount == null ? 0 : mcqCount;

  useEffect(() => () => {
    if (copiedTimer.current) clearTimeout(copiedTimer.current);
  }, []);

  const copyStudentRow = async (student, matched) => {
    const text = studentEdupageClipboard({
      student,
      matched,
      mcqCount: questionCount
    });
    const ok = await copyToClipboard(text);
    const id = String(matched.id);
    setCopiedId(ok ? id : `fail:${id}`);
    if (copiedTimer.current) clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(() => setCopiedId(null), 1500);
  };

  return (
    <div style={fillHeight ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' } : undefined}>
      <style>{`
        .mcq-class-table {
          border-collapse: separate;
          border-spacing: 0;
        }
        .mcq-class-table thead th {
          position: sticky;
          top: 0;
          z-index: 4;
          background: #f8fafc;
          box-shadow: inset 0 -2px 0 #d1d5db;
        }
        .mcq-class-table tbody tr:hover > td {
          background-color: rgba(59,130,246,0.06);
        }
        ${hoveredColIdx != null ? `
          .mcq-class-table tr > *:nth-child(${hoveredColIdx + 1}) {
            background-image: linear-gradient(rgba(37,99,235,0.18), rgba(37,99,235,0.18)) !important;
          }
        ` : ''}
      `}</style>

      <div style={fillHeight ? { flex: 1, minHeight: 0, overflow: 'auto' } : undefined}>
        <table
          className="mcq-class-table"
          onMouseOver={(e) => {
            const cell = e.target.closest && e.target.closest('th, td');
            if (!cell || !cell.parentElement) return;
            const idx = Array.from(cell.parentElement.children).indexOf(cell);
            if (idx >= 0 && idx !== hoveredColIdx) setHoveredColIdx(idx);
          }}
          onMouseLeave={() => { setHoveredColIdx(null); setHoveredRowId(null); }}
          style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '0.78rem' }}
        >
          <thead>
            <tr style={{ background: 'var(--surface, #f1f5f9)' }}>
              <th style={thStyle} />
              <th style={thStyle}>#</th>
              <th style={thStyle}>Student</th>
              <th style={thStyle}>Roll</th>
              <th style={thStyle}>AI name</th>
              <th style={thStyle}>Sheet</th>
              <th style={{ ...thStyle, ...varColEdge, textAlign: 'center' }}>Var</th>
              {Array.from({ length: questionCount }, (_, i) => (
                <th key={i + 1} style={{ ...thStyle, minWidth: 26, textAlign: 'center' }}>{i + 1}</th>
              ))}
              <th style={{ ...thStyle, textAlign: 'center' }} title="EduPage comment">Cmt</th>
              <th style={{ ...thStyle, textAlign: 'center' }} title="EduPage State will be set to Evaluated">Evaluate</th>
              <th style={{ ...thStyle, textAlign: 'center' }} title="a = absent">Points</th>
            </tr>
          </thead>
          <tbody>
            {students.map((s, idx) => {
              const matched = match.byStudent.get(s.id);
              const sRoll = (studentRollDisplay(s));
              const cleanStudentName = stripRollFromName(s.student_name);
              const aiName = matched?.student_name || '';
              const isHovered = hoveredRowId === s.id;
              const baseBg = matched ? 'transparent' : 'rgba(239,68,68,0.04)';
              const hoverBg = matched ? 'rgba(59,130,246,0.10)' : 'rgba(239,68,68,0.18)';
              const absent = isAbsent(matched, questionCount);
              const vTone = matched ? variantTone(matched.paper_variant, maxVariant) : null;
              return (
                <tr
                  key={s.id}
                  onMouseEnter={() => setHoveredRowId(s.id)}
                  onMouseLeave={() => setHoveredRowId(prev => prev === s.id ? null : prev)}
                  style={{ background: isHovered ? hoverBg : baseBg, borderBottom: '1px solid #f1f5f9' }}
                >
                  <td style={tdStyle}>
                    {matched && matched.omr_scan_id ? (
                      <input
                        type="checkbox"
                        title="Select for recheck"
                        checked={selectedRowIds.has(matched.id)}
                        onChange={() => onToggleRow(matched.id)}
                        style={{ cursor: 'pointer' }}
                      />
                    ) : null}
                  </td>
                  <td style={tdStyle}>{idx + 1}</td>
                  <td style={tdStyle}>{s.student_name}</td>
                  <td style={{ ...tdStyle, fontWeight: 600, textTransform: 'uppercase' }}>{sRoll}</td>
                  <td style={tdStyle}>{matched ? (aiName || '—') : ''}</td>
                  <td style={tdStyle}>
                    {matched ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <button
                          type="button"
                          onClick={() => copyStudentRow(s, matched)}
                          title="Copy this student's MCQ answers, comment, and points to the clipboard"
                          style={copiedId === String(matched.id) ? copyBtnDone : copyBtn}
                        >
                          {copiedId === String(matched.id)
                            ? 'Copied'
                            : copiedId === `fail:${matched.id}`
                              ? 'Failed'
                              : 'Copy'}
                        </button>
                        <span style={{ color: '#10b981' }} title={matched.omr_file_name || ''}>
                          <FontAwesomeIcon icon={faCheck} />
                        </span>
                        <button
                          onClick={() => onUnmatch(matched.id)}
                          title="Wrong match — send this sheet back to the problem list so its roll can be corrected"
                          style={unmatchBtn}
                        >
                          Unmatch
                        </button>
                        {(matched.omr_scan_id || matched.text_scan_id) && (
                          <button
                            onClick={() => openPreview(matched, 'omr')}
                            title="View scanned sheet"
                            style={eyeBtn}
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </button>
                        )}
                      </span>
                    ) : (
                      <span style={{ color: '#dc2626' }}>not found</span>
                    )}
                  </td>
                  <td style={{ ...tdStyle, ...varColEdge, textAlign: 'center', background: vTone ? vTone.bg : 'transparent', padding: 0 }}>
                    {matched ? (
                      <EditableCell
                        rowKey={String(matched.id)}
                        colKey="var"
                        value={matched.paper_variant}
                        color={vTone.color}
                        onCommit={(v) => onUpdateField(matched.id, 'paper_variant', v.trim().toUpperCase())}
                      />
                    ) : ''}
                  </td>
                  {Array.from({ length: questionCount }, (_, i) => {
                    const q = String(i + 1);
                    const a = matched?.answers?.[q];
                    const display = a == null || a === '' ? '' : String(a);
                    const tone = answerTone(display, !!matched);
                    return (
                      <td key={q} style={{ ...tdStyle, textAlign: 'center', background: tone.bg, padding: 0 }}>
                        {matched ? (
                          <EditableCell
                            rowKey={String(matched.id)}
                            colKey={q}
                            value={display}
                            color={tone.color}
                            fontWeight={display ? 600 : 400}
                            onCommit={(v) => onUpdateAnswer(matched.id, q, v)}
                          />
                        ) : ''}
                      </td>
                    );
                  })}
                  <td title={buildComment(cleanStudentName, matched?.omr_file_name, absent)} style={{ ...tdStyle, maxWidth: 180 }}>
                    <span style={ellipsis}>
                      {buildComment(cleanStudentName, matched?.omr_file_name, absent)}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    {matched && needsEvaluation(matched.answers, questionCount) ? (
                      <span style={evaluateChip}>Evaluate</span>
                    ) : ''}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 700, color: '#dc2626' }}>
                    {absent ? 'a' : ''}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function studentRollDisplay(s) {
  const name = String(s.student_name || '');
  const m = name.match(/\(([^)]+)\)/);
  return (m ? m[1] : (s.roll_number || '')).toString().trim();
}

const thStyle = {
  padding: '8px 6px',
  textAlign: 'left',
  fontWeight: 700,
  fontSize: '0.68rem',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: '#475569',
  borderBottom: '2px solid #d1d5db',
  whiteSpace: 'nowrap',
  position: 'sticky',
  top: 0,
  background: '#f8fafc',
  zIndex: 2
};

// Heavier right edge so Var stays visually apart from answer columns 1–30.
const varColEdge = {
  borderRight: '3px solid #334155'
};

const tdStyle = {
  padding: '6px 8px',
  fontSize: '0.78rem',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
  borderBottom: '1px solid rgba(148,163,184,0.18)',
  borderRight: '1px solid rgba(148,163,184,0.18)'
};

const copyBtn = {
  padding: '3px 8px',
  fontSize: '0.72rem',
  fontWeight: 600,
  border: '1px solid #0369a1',
  borderRadius: 4,
  background: 'white',
  color: '#0369a1',
  cursor: 'pointer'
};

const copyBtnDone = {
  ...copyBtn,
  border: '1px solid #059669',
  color: '#059669'
};

const unmatchBtn = {
  padding: '3px 8px',
  fontSize: '0.72rem',
  fontWeight: 600,
  border: '1px solid #dc2626',
  borderRadius: 4,
  background: 'white',
  color: '#dc2626',
  cursor: 'pointer'
};

const eyeBtn = {
  padding: '3px 8px',
  fontSize: '0.78rem',
  border: '1px solid #10b981',
  borderRadius: 4,
  background: 'white',
  color: '#10b981',
  cursor: 'pointer'
};

const ellipsis = {
  display: 'inline-block',
  maxWidth: 140,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle'
};

const evaluateChip = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 10,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.4px',
  textTransform: 'uppercase',
  background: '#f59e0b',
  color: 'white'
};
